import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { useEntitlements } from '@/hooks/useEntitlements';

/**
 * Subscription lifecycle strip (past_due / unpaid / read-only). Shown app-wide
 * until billing recovers. Uses app-shell entitlements (no separate /billing/subscription fetch).
 */
export function LifecycleBanner() {
  const { t } = useTranslation('settings');
  const { subscription, pendingDeletion, loading } = useEntitlements();
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState<'readonly' | 'grace' | 'payment'>('payment');

  useEffect(() => {
    if (loading || !subscription || pendingDeletion) {
      setVisible(false);
      return;
    }
    const ro = Boolean(subscription.readOnlyMode);
    const pastDue = subscription.status === 'past_due';
    const unpaid = subscription.status === 'unpaid';
    if (ro || unpaid) {
      setVariant(ro ? 'readonly' : 'grace');
      setVisible(true);
      return;
    }
    if (pastDue) {
      setVariant('payment');
      setVisible(true);
      return;
    }
    setVisible(false);
  }, [subscription, loading, pendingDeletion]);

  if (!visible) return null;

  const bg =
    variant === 'readonly'
      ? 'bg-red-950/90 border-red-500/40 text-red-50'
      : variant === 'grace'
        ? 'bg-amber-950/90 border-amber-500/35 text-amber-50'
        : 'bg-slate-900/95 border-slate-600/40 text-slate-100';

  const messageKey =
    variant === 'readonly'
      ? 'lifecycle.banner.readonly'
      : variant === 'grace'
        ? 'lifecycle.banner.unpaid'
        : 'lifecycle.banner.pastDue';

  return (
    <div
      role="status"
      className={`flex shrink-0 items-center justify-center gap-2 border-b px-4 py-2.5 text-center text-xs font-semibold sm:text-sm ${bg}`}
    >
      <AlertTriangle className="hidden h-4 w-4 shrink-0 sm:inline" aria-hidden />
      <span>{t(messageKey)}</span>
      <Link to="/settings/subscription" className="underline underline-offset-2 hover:opacity-90">
        {t('lifecycle.banner.cta')}
      </Link>
    </div>
  );
}
